import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

const PrivacyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Privacy Policy — rawResponse {Labs}"
        description="How Raw Response Labs collects, uses, and protects personal data across our website and services."
      />
      <Navbar />

      <main className="pt-24 pb-20">
        <div className="container max-w-3xl">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-[13px] font-mono text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={14} />
            {t("privacy.back")}
          </a>

          <h1 className="text-3xl md:text-4xl font-mono font-bold mb-2">{t("privacy.title")}</h1>
          <p className="text-[12px] font-mono text-muted-foreground/60 mb-10">{t("privacy.lastUpdated")}</p>

          <div className="space-y-8 text-[14px] font-mono text-muted-foreground leading-relaxed">
            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">1. Data Controller</h2>
              <p>The data controller responsible for this website is:</p>
              <p className="mt-2">Raw Response Labs UG (haftungsbeschränkt)</p>
              <p>Urbanstr. 71, 10967 Berlin, Germany</p>
              <p>
                Email:{" "}
                <a href="mailto:hello@valente.ventures" className="text-primary hover:text-primary/80 transition-colors">
                  hello@valente.ventures
                </a>
              </p>
              <p>Phone: +49 (0) 1520 / 8544 838</p>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">2. What Data We Collect</h2>
              <p>We collect and process the following data:</p>
              <ul className="list-disc list-inside mt-2 space-y-1.5">
                <li><strong className="text-foreground">Account data:</strong> Email address and display name provided during registration.</li>
                <li><strong className="text-foreground">Website data:</strong> Publicly available content from URLs you submit for analysis, including page text, meta descriptions, and headings.</li>
                <li><strong className="text-foreground">Tracking data:</strong> AI platform responses to prompts we execute on your behalf, including mention detection, sentiment, citations, and competitor analysis.</li>
                <li><strong className="text-foreground">Generated content:</strong> Article drafts, content briefs, and strategic recommendations created by our AI systems for your use.</li>
                <li><strong className="text-foreground">Usage data:</strong> Feature usage counts for plan limit enforcement.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">3. How We Use Your Data</h2>
              <p>Your data is used exclusively to provide the Raw Response Labs service:</p>
              <ul className="list-disc list-inside mt-2 space-y-1.5">
                <li>To analyze your brand's visibility across AI platforms</li>
                <li>To generate tracking prompts and execute them on AI platforms</li>
                <li>To create content recommendations and article drafts</li>
                <li>To monitor competitor presence and market positioning</li>
                <li>To enforce subscription plan limits</li>
                <li>To send automated tracking reports (if enabled)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">4. Legal Basis (GDPR Art. 6)</h2>
              <p>We process your data based on:</p>
              <ul className="list-disc list-inside mt-2 space-y-1.5">
                <li><strong className="text-foreground">Contract performance</strong> (Art. 6(1)(b)): Processing necessary to provide the service you signed up for.</li>
                <li><strong className="text-foreground">Legitimate interest</strong> (Art. 6(1)(f)): Service improvement and security measures.</li>
                <li><strong className="text-foreground">Consent</strong> (Art. 6(1)(a)): For optional features like email reports, which you can withdraw at any time.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">5. Third-Party Services</h2>
              <p>We use the following third-party services:</p>
              <div className="mt-3 space-y-4">
                <div>
                  <h3 className="text-foreground font-medium text-[14px]">Google Cloud Platform</h3>
                  <p className="text-[13px]">Database hosting, authentication, and AI services. Data is processed in the EU.</p>
                </div>
                <div>
                  <h3 className="text-foreground font-medium text-[14px]">OpenAI (ChatGPT)</h3>
                  <p className="text-[13px]">We send tracking prompts to OpenAI's API. Only the prompt text is sent — no personal data.</p>
                </div>
                <div>
                  <h3 className="text-foreground font-medium text-[14px]">Anthropic (Claude)</h3>
                  <p className="text-[13px]">We send tracking prompts to Anthropic's API. Only the prompt text is sent.</p>
                </div>
                <div>
                  <h3 className="text-foreground font-medium text-[14px]">Perplexity AI</h3>
                  <p className="text-[13px]">We send tracking prompts to Perplexity's API. Only the prompt text is sent.</p>
                </div>
                <div>
                  <h3 className="text-foreground font-medium text-[14px]">Resend</h3>
                  <p className="text-[13px]">Email delivery for automated reports. Receives your email address and report content.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">6. Data We Do Not Collect</h2>
              <ul className="list-disc list-inside space-y-1.5">
                <li>We do not use cookies for tracking or advertising</li>
                <li>We do not use analytics tools (Google Analytics, etc.)</li>
                <li>We do not sell, rent, or share your data with advertisers</li>
                <li>We do not use your data to train AI models</li>
                <li>We do not access private or authenticated pages on your website</li>
              </ul>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">7. Data Storage & Security</h2>
              <p>
                Your data is stored with encryption at rest (AES-256) and in transit (TLS 1.2+).
                Authentication uses industry-standard security practices. Access to production systems
                is restricted to authorized personnel only.
              </p>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">8. Data Retention</h2>
              <p>
                We retain your data for as long as your account is active. When you delete data from your
                dashboard, all associated data is permanently removed. To delete your entire account,
                contact us at{" "}
                <a href="mailto:hello@valente.ventures" className="text-primary hover:text-primary/80 transition-colors">
                  hello@valente.ventures
                </a>{" "}
                and we will process the deletion within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">9. Your Rights (GDPR)</h2>
              <p>Under the General Data Protection Regulation, you have the right to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1.5">
                <li><strong className="text-foreground">Access</strong> your personal data (Art. 15)</li>
                <li><strong className="text-foreground">Rectify</strong> inaccurate data (Art. 16)</li>
                <li><strong className="text-foreground">Erase</strong> your data (Art. 17)</li>
                <li><strong className="text-foreground">Restrict</strong> processing (Art. 18)</li>
                <li><strong className="text-foreground">Data portability</strong> — receive your data in a structured format (Art. 20)</li>
                <li><strong className="text-foreground">Object</strong> to processing (Art. 21)</li>
                <li><strong className="text-foreground">Lodge a complaint</strong> with a supervisory authority</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, contact us at{" "}
                <a href="mailto:hello@valente.ventures" className="text-primary hover:text-primary/80 transition-colors">
                  hello@valente.ventures
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="text-foreground font-semibold text-[15px] mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. Changes will be posted on this page
                with an updated revision date. For significant changes, we will notify you via email.
              </p>
            </section>

            <div className="section-divider w-full my-10" />

            <p className="text-[12px] text-muted-foreground/50">
              © {new Date().getFullYear()} Valente Ventures GmbH. All rights reserved.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
